import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAmazonRefunds, createAmazonRefund, reviewAmazonRefund, getAmazonAccounts, getStaff } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, CheckCircle, XCircle, Image, Clock, AmazonLogo, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_BASE = window.location.origin;

export default function AmazonRefundsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdminOrAbove = isOwner || user?.role === 'assistant';
  const isAdmin = isAdminOrAbove;
  const [refunds, setRefunds] = useState([]);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [amount, setAmount] = useState('');
  const [amazonAccount, setAmazonAccount] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [bookingRef, setBookingRef] = useState('');
  const [note, setNote] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const statusFilter = tab === 'all' ? undefined : tab;
      const [refundsRes, accountsRes] = await Promise.all([
        getAmazonRefunds(undefined, statusFilter),
        getAmazonAccounts()
      ]);
      setRefunds(refundsRes.data);
      setSavedAccounts(accountsRes.data);
    } catch (error) {
      toast.error('Failed to fetch refunds');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be under 5MB');
        return;
      }
      setScreenshot(file);
      setScreenshotPreview(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setAmount('');
    setAmazonAccount('');
    setScreenshot(null);
    setScreenshotPreview(null);
    setBookingRef('');
    setNote('');
  };

  if (!isAdminOrAbove) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!screenshot) { toast.error('Screenshot is required'); return; }
    if (!amazonAccount.trim()) { toast.error('Amazon account is required'); return; }
    if (!amount || parseFloat(amount) <= 0) { toast.error('Valid amount is required'); return; }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', parseFloat(amount));
      formData.append('amazon_account', amazonAccount.trim());
      formData.append('screenshot', screenshot);
      if (bookingRef.trim()) formData.append('booking_reference', bookingRef.trim());
      if (note.trim()) formData.append('note', note.trim());

      await createAmazonRefund(formData);
      toast.success('Amazon refund submitted! Waiting for admin approval.');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit refund');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReview = async (refundId, action) => {
    if (action === 'reject' && !window.confirm('Reject this refund?')) return;
    try {
      await reviewAmazonRefund(refundId, { action });
      toast.success(`Refund ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} refund`);
    }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amt || 0);

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return <span className={`text-xs px-2 py-0.5 ${map[status] || 'bg-gray-100'}`} data-testid="refund-status-badge">{status?.charAt(0).toUpperCase() + status?.slice(1)}</span>;
  };

  const myAccounts = savedAccounts.filter(a => !isAdmin || a.staff_id === user?.id).map(a => a.account);
  const pendingCount = refunds.filter(r => r.status === 'pending').length;
  const approvedTotal = refunds.filter(r => r.status === 'approved').reduce((s, r) => s + r.amount, 0);

  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: `Pending${pendingCount ? ` (${pendingCount})` : ''}` },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' }
  ];

  return (
    <div className="space-y-6" data-testid="amazon-refunds-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AmazonLogo size={28} weight="fill" className="text-[#FF9900]" />
            Amazon Refunds
          </h1>
          <p className="text-sm text-gray-500 mt-1">Submit refund with screenshot proof for admin verification</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="btn-primary" data-testid="submit-refund-btn">
          <Plus size={18} className="mr-2" /> Submit Refund
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 p-4" data-testid="stat-total">
          <div className="text-xs text-gray-500">Total Refunds</div>
          <div className="text-xl font-bold text-gray-900">{refunds.length}</div>
        </div>
        <div className="bg-white border border-gray-200 p-4" data-testid="stat-pending">
          <div className="text-xs text-gray-500">Pending Approval</div>
          <div className="text-xl font-bold text-amber-600">{pendingCount}</div>
        </div>
        <div className="bg-white border border-gray-200 p-4" data-testid="stat-approved-total">
          <div className="text-xs text-gray-500">Approved Total</div>
          <div className="text-xl font-bold text-green-600">{formatCurrency(approvedTotal)}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                {isAdmin && <TableHead>Staff</TableHead>}
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Amazon Account</TableHead>
                <TableHead>Screenshot</TableHead>
                <TableHead>Ref</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                {isAdmin && <TableHead className="text-center">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {refunds.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 8 : 6} className="text-center text-gray-500 py-8">
                    No refunds found
                  </TableCell>
                </TableRow>
              ) : (
                refunds.map((r) => (
                  <TableRow key={r.id} data-testid={`amazon-refund-row-${r.id}`}>
                    {isAdmin && <TableCell className="font-medium">{r.staff_name}</TableCell>}
                    <TableCell className="numeric font-semibold">{formatCurrency(r.amount)}</TableCell>
                    <TableCell className="text-sm text-gray-700 max-w-[180px] truncate">{r.amazon_account}</TableCell>
                    <TableCell>
                      {r.screenshot_url ? (
                        <button
                          onClick={() => setPreviewImg(`${API_BASE}${r.screenshot_url}`)}
                          className="text-[#002FA7] hover:underline text-xs flex items-center gap-1"
                          data-testid={`view-screenshot-${r.id}`}
                        >
                          <Eye size={14} /> View
                        </button>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">{r.booking_reference || '-'}</TableCell>
                    <TableCell>{statusBadge(r.status)}</TableCell>
                    <TableCell className="text-xs text-gray-500">{formatDateTime(r.created_at)}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-center">
                        {r.status === 'pending' ? (
                          <div className="flex gap-1 justify-center">
                            <Button
                              size="sm"
                              onClick={() => handleReview(r.id, 'approve')}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2"
                              data-testid={`approve-refund-${r.id}`}
                            >
                              <CheckCircle size={14} className="mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReview(r.id, 'reject')}
                              className="text-red-600 border-red-300 hover:bg-red-50 text-xs h-7 px-2"
                              data-testid={`reject-refund-${r.id}`}
                            >
                              <XCircle size={14} />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">
                            {r.reviewed_by && `by ${r.reviewed_by}`}
                          </span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Submit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AmazonLogo size={22} weight="fill" className="text-[#FF9900]" />
              Submit Amazon Refund
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="form-label">Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter refund amount"
                required
                data-testid="refund-amount-input"
              />
            </div>

            <div>
              <Label className="form-label">Amazon Account ID / Email *</Label>
              <Input
                value={amazonAccount}
                onChange={(e) => setAmazonAccount(e.target.value)}
                placeholder="e.g., user@email.com or Account ID"
                required
                list="saved-accounts"
                data-testid="refund-amazon-account-input"
              />
              {myAccounts.length > 0 && (
                <datalist id="saved-accounts">
                  {myAccounts.map((a) => <option key={a} value={a} />)}
                </datalist>
              )}
            </div>

            <div>
              <Label className="form-label">Screenshot Proof *</Label>
              <div className="border-2 border-dashed border-gray-300 p-4 text-center hover:border-[#002FA7] transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  className="hidden"
                  id="screenshot-upload"
                  data-testid="refund-screenshot-input"
                />
                <label htmlFor="screenshot-upload" className="cursor-pointer">
                  {screenshotPreview ? (
                    <img src={screenshotPreview} alt="Preview" className="max-h-32 mx-auto mb-2 object-contain" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <Image size={32} />
                      <span className="text-sm">Click to upload screenshot</span>
                    </div>
                  )}
                </label>
                {screenshot && (
                  <p className="text-xs text-green-600 mt-1">{screenshot.name}</p>
                )}
              </div>
              {!screenshot && <p className="text-xs text-red-500 mt-1">Screenshot is mandatory</p>}
            </div>

            <div>
              <Label className="form-label">Booking Reference (Optional)</Label>
              <Input
                value={bookingRef}
                onChange={(e) => setBookingRef(e.target.value)}
                placeholder="e.g., booking ID or order number"
                data-testid="refund-booking-ref-input"
              />
            </div>

            <div>
              <Label className="form-label">Note (Optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
                data-testid="refund-note-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={submitting || !screenshot || !amazonAccount.trim() || !amount}
              data-testid="refund-submit-form-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Refund for Approval'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Screenshot Preview Modal */}
      {previewImg && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImg(null)}
          data-testid="screenshot-preview-modal"
        >
          <div className="bg-white p-2 max-w-2xl max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <img src={previewImg} alt="Refund Screenshot" className="max-w-full" />
            <Button
              variant="outline"
              className="w-full mt-2"
              onClick={() => setPreviewImg(null)}
              data-testid="close-preview-btn"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
